import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';

var avatarPalettes = [
  { background: '#f5e6d8', color: '#b87040' },
  { background: '#faf0e8', color: '#c07838' },
  { background: '#eeddd0', color: '#a86030' },
  { background: '#f7ece0', color: '#d5a67b' },
  { background: '#f0e0cc', color: '#b87848' },
];

function getInitials(name) {
  if (!name) return '?';
  var parts = name.trim().split(' ').filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

function getAvatarStyle(name) {
  var idx = name ? name.charCodeAt(0) % avatarPalettes.length : 0;
  return avatarPalettes[idx];
}

function getPaymentStatusStyle(status) {
  var s = (status || '').toString().toLowerCase();
  if (s === 'paid' || s === 'success' || s === 'completed') return { label: status, color: '#27ae60', bg: '#eafaf1' };
  if (s === 'pending') return { label: status, color: '#e67e22', bg: '#fef5ec' };
  if (s === 'failed' || s === 'refunded' || s === 'cancelled') return { label: status, color: '#e74c3c', bg: '#fdf0ef' };
  return { label: status || 'N/A', color: '#888', bg: '#f5f5f5' };
}

function getButtonStyle(variant) {
  var base = {
    padding: '8px 14px',
    fontSize: '12px',
    fontWeight: '600',
    borderRadius: '6px',
    cursor: 'pointer',
    border: '1px solid transparent',
    whiteSpace: 'nowrap',
  };
  if (variant === 'primary') return Object.assign({}, base, { background: '#d5a67b', color: '#fff', borderColor: '#d5a67b' });
  if (variant === 'danger') return Object.assign({}, base, { background: '#fff', color: '#e74c3c', borderColor: '#e74c3c' });
  if (variant === 'success') return Object.assign({}, base, { background: '#27ae60', color: '#fff', borderColor: '#27ae60' });
  if (variant === 'warning') return Object.assign({}, base, { background: '#e67e22', color: '#fff', borderColor: '#e67e22' });
  return Object.assign({}, base, { background: '#f5f0eb', color: '#485257', borderColor: '#e8ddd6' });
}

function InfoRow(props) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '9px 0', borderBottom: '1px solid #f0ebe5', gap: '12px' }}>
      <span style={{ fontSize: '12px', color: '#999', fontWeight: '500', flexShrink: 0, minWidth: '120px' }}>{props.label}</span>
      <span style={{ fontSize: '13px', color: '#485257', fontWeight: '500', textAlign: 'right', wordBreak: 'break-word' }}>{props.value || '-'}</span>
    </div>
  );
}

function PersonBlock(props) {
  var name = props.name;
  var email = props.email;
  var image = props.image;
  var onClick = props.onClick;
  var avatarStyle = getAvatarStyle(name);
  return (
    <div
      onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 0', borderBottom: '1px solid #f0ebe5', cursor: onClick ? 'pointer' : 'default' }}
    >
      <div style={{ width: 36, height: 36, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '13px', background: image ? 'transparent' : avatarStyle.background, color: avatarStyle.color }}>
        {image ? (
          <img src={process.env.REACT_APP_IMAGE_URL + image} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : getInitials(name)}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: '600', color: onClick ? '#d5a67b' : '#485257', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: onClick ? 'underline' : 'none' }}>{name || 'N/A'}</div>
        {email && <div style={{ fontSize: '12px', color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{email}</div>}
      </div>
    </div>
  );
}

export default function AppointmentInfoPanel(props) {
  var appointment = props.appointment;
  var onClose = props.onClose;
  var clientLabel = props.clientLabel || 'Client';
  var viewType = props.viewType || 'appointment';
  var navigate = useNavigate();

  var isOpen = appointment ? true : false;
  useEffect(function() {
    if (!isOpen) {
      document.documentElement.style.overflow = '';
      return;
    }
    document.documentElement.style.overflow = 'hidden';
    return function() {
      document.documentElement.style.overflow = '';
    };
  }, [isOpen]);

  if (!appointment) return null;

  var goToMediumProfile = appointment.medium_id ? function() {
    onClose();
    if (props.onMediumClick) {
      props.onMediumClick(appointment);
    } else {
      navigate('/profile-edit/' + appointment.medium_id);
    }
  } : undefined;

  var goToClientProfile = appointment.client_id ? function() {
    onClose();
    if (props.onClientClick) {
      props.onClientClick(appointment);
    } else if (appointment.client_user_type === 3) {
      navigate('/edit/' + appointment.client_id);
    } else {
      navigate('/profile-edit/' + appointment.client_id);
    }
  } : undefined;

  if (viewType === 'medium') {
    var md = appointment.medium_details || {};
    var mdDisplayName = md.screen_name || md.full_name || 'N/A';
    var mdAvatarStyle = getAvatarStyle(mdDisplayName);
    
    var mdStatusStyle = md.status === 'Approved' ? { color: '#27ae60', bg: '#eafaf1' } :
                        md.status === 'Declined' ? { color: '#e74c3c', bg: '#fdf0ef' } :
                        md.status === 'Restricted' ? { color: '#e67e22', bg: '#fef5ec' } :
                        { color: '#888', bg: '#f5f5f5' };

    return ReactDOM.createPortal(
      <div>
        {/* Backdrop */}
        <div
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.25)',
            zIndex: 9998,
          }}
        />

        {/* Slide-in panel */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            height: '100%',
            width: window.innerWidth <= 600 ? '100vw' : '360px',
            maxWidth: '100vw',
            background: '#fff',
            zIndex: 9999,
            boxShadow: '-6px 0 32px rgba(0,0,0,0.14)',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {/* Header */}
          <div style={{ background: '#f8f4f0', padding: '18px 20px 14px', borderBottom: '1px solid #e8ddd6', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div 
                onClick={goToMediumProfile}
                style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0, cursor: goToMediumProfile ? 'pointer' : 'default' }}
              >
                <div style={Object.assign({}, mdAvatarStyle, {
                  width: 46,
                  height: 46,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '700',
                  fontSize: '17px',
                  flexShrink: 0,
                })}>
                  {getInitials(mdDisplayName)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: '#d5a67b', marginBottom: '2px' }}>
                    Medium Information
                  </div>
                  <div className="profile-name-link" style={{ fontSize: '15px', fontWeight: '700', color: '#485257', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: goToMediumProfile ? 'underline' : 'none' }}>
                    {mdDisplayName}
                  </div>
                  <div style={{ fontSize: '12px', color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {md.email}
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: '22px', lineHeight: 1, padding: '4px', flexShrink: 0, alignSelf: 'flex-start' }}
              >
                ×
              </button>
            </div>
          </div>

          {/* Info section */}
          <div style={{ padding: '4px 20px 8px', flex: 1 }}>
            {md.full_name && <InfoRow label="Full Name" value={md.full_name} />}
            <InfoRow label="Approved Role" value={md.approved_role || '-'} />
            <InfoRow
              label="Status"
              value={
                <span style={{ background: mdStatusStyle.bg, color: mdStatusStyle.color, padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                  {md.status || 'Pending'}
                </span>
              }
            />
            <InfoRow label="Online Status" value={md.online_status || '-'} />
            <InfoRow label="Test User" value={md.test_user || 'Off'} />
            <InfoRow label="Starter Referral Code" value={md.starter_referral_code || '-'} />
            <InfoRow label="Total Starter Referrals" value={md.total_starter_referrals !== undefined ? String(md.total_starter_referrals) : '-'} />
            {md.phone && <InfoRow label="Phone" value={md.phone} />}
            {md.whatsapp && <InfoRow label="WhatsApp" value={md.whatsapp} />}
            {md.country && <InfoRow label="Country" value={md.country} />}
            {md.timezone && <InfoRow label="Timezone" value={md.timezone} />}
            {md.registered && <InfoRow label="Registered" value={md.registered} />}
            <InfoRow
              label="Zoom"
              value={
                <span style={{ background: md.zoom === 'Connected' ? '#eafaf1' : '#fdf0ef', color: md.zoom === 'Connected' ? '#27ae60' : '#e74c3c', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                  {md.zoom || 'Not Connected'}
                </span>
              }
            />
            <InfoRow
              label="Google Calendar"
              value={
                <span style={{ background: md.google_calendar === 'Connected' ? '#eafaf1' : '#fdf0ef', color: md.google_calendar === 'Connected' ? '#27ae60' : '#e74c3c', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                  {md.google_calendar || 'Not Connected'}
                </span>
              }
            />
            <InfoRow label="Slots Last Generated" value={md.slots_last_generated || '-'} />
          </div>
        </div>
      </div>,
      document.body
    );
  }

  if (viewType === 'client') {
    var cd = appointment.client_details || {};
    var cdDisplayName = cd.screen_name || cd.full_name || 'N/A';
    var cdAvatarStyle = getAvatarStyle(cdDisplayName);

    var cdStatusStyle = cd.status === 'Active' ? { color: '#27ae60', bg: '#eafaf1' } :
                        cd.status === 'Inactive' ? { color: '#e74c3c', bg: '#fdf0ef' } :
                        cd.status === 'Restricted' ? { color: '#e67e22', bg: '#fef5ec' } :
                        { color: '#27ae60', bg: '#eafaf1' };

    return ReactDOM.createPortal(
      <div>
        {/* Backdrop */}
        <div
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.25)',
            zIndex: 9998,
          }}
        />

        {/* Slide-in panel */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            height: '100%',
            width: window.innerWidth <= 600 ? '100vw' : '360px',
            maxWidth: '100vw',
            background: '#fff',
            zIndex: 9999,
            boxShadow: '-6px 0 32px rgba(0,0,0,0.14)',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {/* Header */}
          <div style={{ background: '#f8f4f0', padding: '18px 20px 14px', borderBottom: '1px solid #e8ddd6', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div 
                onClick={goToClientProfile}
                style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0, cursor: goToClientProfile ? 'pointer' : 'default' }}
              >
                <div style={Object.assign({}, cdAvatarStyle, {
                  width: 46,
                  height: 46,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '700',
                  fontSize: '17px',
                  flexShrink: 0,
                })}>
                  {getInitials(cdDisplayName)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: '#d5a67b', marginBottom: '2px' }}>
                    Client Information
                  </div>
                  <div className="profile-name-link" style={{ fontSize: '15px', fontWeight: '700', color: '#485257', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: goToClientProfile ? 'underline' : 'none' }}>
                    {cdDisplayName}
                  </div>
                  <div style={{ fontSize: '12px', color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {cd.email}
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: '22px', lineHeight: 1, padding: '4px', flexShrink: 0, alignSelf: 'flex-start' }}
              >
                ×
              </button>
            </div>
          </div>

          {/* Info section */}
          <div style={{ padding: '4px 20px 8px', flex: 1 }}>
            {cd.full_name && <InfoRow label="Full Name" value={cd.full_name} />}
            <InfoRow
              label="Status"
              value={
                <span style={{ background: cdStatusStyle.bg, color: cdStatusStyle.color, padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                  {cd.status || 'Active'}
                </span>
              }
            />
            <InfoRow label="Test User" value={cd.test_user || 'Off'} />
            <InfoRow label="Referral Code" value={cd.referral_code || '-'} />
            {cd.tags && cd.tags !== '-' && <InfoRow label="Tags" value={cd.tags} />}
            {cd.phone && <InfoRow label="Phone" value={cd.phone} />}
            {cd.whatsapp && <InfoRow label="WhatsApp" value={cd.whatsapp} />}
            {cd.country && <InfoRow label="Country" value={cd.country} />}
            {cd.timezone && <InfoRow label="Timezone" value={cd.timezone} />}
            {cd.created_at && <InfoRow label="Created" value={cd.created_at} />}
          </div>
        </div>
      </div>,
      document.body
    );
  }

  var sessionIdDisplay = appointment.sessionIdDisplay || appointment.id;
  var payment = getPaymentStatusStyle(appointment.payment_status);
  var isGroupSession = appointment.is_group_session === 1;
  var actions = props.actions || [];

  return ReactDOM.createPortal(
    <div>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.25)',
          zIndex: 9998,
        }}
      />

      {/* Slide-in panel */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100%',
          width: window.innerWidth <= 600 ? '100vw' : '360px',
          maxWidth: '100vw',
          background: '#fff',
          zIndex: 9999,
          boxShadow: '-6px 0 32px rgba(0,0,0,0.14)',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Header */}
        <div style={{ background: '#f8f4f0', padding: '18px 20px 14px', borderBottom: '1px solid #e8ddd6', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: '#d5a67b', marginBottom: '2px' }}>
                Appointment Information
              </div>
              <div style={{ fontSize: '15px', fontWeight: '700', color: '#485257', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {sessionIdDisplay}
              </div>
              <div style={{ fontSize: '12px', color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {appointment.session_name || 'N/A'}
              </div>
            </div>
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: '22px', lineHeight: 1, padding: '4px', flexShrink: 0, alignSelf: 'flex-start' }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Info section */}
        <div style={{ padding: '4px 20px 8px', flex: 1 }}>
          {appointment.backfill_code !== null && appointment.backfill_code !== undefined && (
            <InfoRow label="Backfill Code" value={appointment.backfill_code} />
          )}
          <InfoRow label="Date" value={appointment.date} />
          <InfoRow label="Time" value={appointment.time} />

          <div style={{ fontSize: '11px', color: '#999', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '10px', marginBottom: '2px' }}>Medium</div>
          <PersonBlock name={appointment.medium_name} email={appointment.medium_email} image={appointment.medium_profile_image} onClick={goToMediumProfile} />

          <div style={{ fontSize: '11px', color: '#999', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '10px', marginBottom: '2px' }}>
            {isGroupSession ? 'Group Session' : clientLabel}
          </div>
          {isGroupSession ? (
            <div>
              <InfoRow label="Attendees" value={Array.isArray(appointment.attendees) ? appointment.attendees.length : 0} />
              <InfoRow label="Workers" value={Array.isArray(appointment.workers) ? appointment.workers.length : 0} />
            </div>
          ) : (
            <PersonBlock name={appointment.client_name} email={appointment.client_email} image={appointment.client_profile_image} onClick={goToClientProfile} />
          )}

          <div style={{ marginTop: '6px' }}>
            <InfoRow
              label="Payment Status"
              value={
                <span style={{ background: payment.bg, color: payment.color, padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                  {payment.label}
                </span>
              }
            />
            <InfoRow label="Meeting Link" value={appointment.meeting_link && appointment.meeting_link !== 'NULL' ? (
              <a href={appointment.meeting_link} target="_blank" rel="noreferrer" style={{ color: '#d5a67b' }}>Open Link</a>
            ) : '-'} />
            <InfoRow label="Guest Booking" value={appointment.guest_booking == 1 ? 'Yes' : 'No'} />
            <InfoRow label="Worker Appointment" value={appointment.is_worker_appointment == 1 ? 'Yes' : 'No'} />
            <InfoRow label="Created At" value={appointment.created_at || appointment.createdDate} />
          </div>

          {appointment.last_reschedule && (
            <div>
              <div style={{ fontSize: '11px', color: '#999', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '10px', marginBottom: '2px' }}>Rescheduled</div>
              <InfoRow label="Rescheduled By" value={appointment.last_reschedule.by_name} />
              <InfoRow label="Rescheduled At" value={appointment.last_reschedule.at} />
              <InfoRow label="Reason" value={appointment.last_reschedule.reason} />
            </div>
          )}
        </div>

        {/* Action buttons */}
        {actions.length > 0 && (
          <div style={{ padding: '14px 20px 20px', borderTop: '1px solid #f0ebe5', flexShrink: 0, display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {actions.map(function(action, i) {
              var style = getButtonStyle(action.variant);
              if (action.disabled) {
                style = Object.assign({}, style, { opacity: 0.6, cursor: 'not-allowed' });
              }
              return (
                <button key={i} onClick={action.onClick} disabled={action.disabled} style={style}>
                  {action.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
